<template>
  <!-- A <div>, not a <button>: children are arbitrary authored components, and
       an author dropping a CTA inside makes the parser auto-close the outer
       button — which surfaces only as a hydration mismatch in the built pipe.
       `role="button"` + `aria-pressed`, not `role="radio"`: a standalone item
       has no radiogroup ancestor to belong to. -->
  <div
    ref="rootEl"
    class="option-select-item"
    role="button"
    :aria-pressed="isSelected"
    tabindex="0"
    :class="{ active: isSelected, 'no-select': isNoSelect }"
    @click="activate"
    @keydown="onKeydown"
  >
    <img
      v-if="imageProps && imageShouldRender"
      v-bind="imageProps.imgAttrs"
      alt=""
      class="osi-bg-image"
      :class="{ 'is-hidden': imageShouldFadeOut }"
      :style="mediaStyle"
    />
    <video
      v-if="videoUrl"
      :src="videoUrl"
      :autoplay="fields.disableVideoAutoplay !== true"
      :loop="fields.disableVideoLoop !== true"
      :muted="fields.playVideoWithSound !== true"
      playsinline
      class="osi-bg-video"
      :style="mediaStyle"
      @canplay="videoReady = true"
    />
    <div v-if="hasHeader" class="osi-band osi-header">
      <RenderSlot name="header" :children="slotChildren?.header" :editable="editable" />
    </div>

    <div class="osi-row">
      <span
        v-if="showIndicator"
        class="indicator"
        :class="[isCheckbox ? 'checkbox' : 'radio', { checked: isSelected }]"
        :style="{ order: indicatorOrder }"
      >
        <svg
          v-if="isCheckbox"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 12.3636L9.75 16L18 8"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
        </svg>
        <span v-else class="radio-dot" />
      </span>

      <div class="osi-content">
        <!-- Two paths, never both: `slotChildren` is only handed down once SOME
             child carries a `slot`, and in that case Vue is passed no default
             slot content at all — so an item authored before this component had
             slots keeps rendering through `<slot />`. -->
        <RenderSlot
          v-if="slotChildren"
          name="default"
          :children="slotChildren.default"
          :editable="editable"
        />
        <slot v-else />
        <p v-if="wiringHint" class="osi-hint">{{ wiringHint }}</p>
      </div>
    </div>

    <div v-if="hasFooter" class="osi-band osi-footer">
      <RenderSlot name="footer" :children="slotChildren?.footer" :editable="editable" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue'
import { useRuntimeConfig } from '#imports'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import { useImagePreset } from '@render/composables/useImagePreset'
import { useSlotHasVisibleChild } from '@render/composables/useSlotVisibility'
import {
  composeShadow,
  getColorStyleData,
  getGradientStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import RenderSlot from '@render/RenderSlot.vue'
import type { ComponentNode } from '@api/types.gen'
import { isOperandReference, resolveOperandValue, valuesMatch } from './optionValue'
import { usePreselect } from './usePreselect'
import { IndicatorSide, SelectionMode } from './types'
import type { OptionSelectItemFields } from './definition'

const emit = defineEmits<{ (e: 'onClick'): void }>()

const props = defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

const fields = defineModel<OptionSelectItemFields>('fields', {
  required: true,
  default: () => ({}),
})

const pipe = usePipe()

// A band renders no element at all unless something inside it will actually
// show, so an item using neither keeps the card box it had before the bands
// existed — corner-radius clipping included, which only switches on for a band
// that really reaches the edge.
const hasHeader = useSlotHasVisibleChild(() => props.slotChildren?.header)
const hasFooter = useSlotHasVisibleChild(() => props.slotChildren?.footer)

const varKey = computed(() => fields.value.targetVariable?.key ?? '')
const isMulti = computed(() => fields.value.selectionMode === SelectionMode.MULTI)
const isCheckbox = computed(() => isMulti.value)
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)
const isNoSelect = computed(() => pipe.currentMode.value === PipeDisplayMode.STATIC)

// Flex `order` on a row box is writing-mode relative, so the side mirrors
// under dir="rtl" with no branch of our own.
const indicatorSide = computed(() => fields.value.indicatorSide ?? IndicatorSide.LEFT)
const showIndicator = computed(() => indicatorSide.value !== IndicatorSide.HIDDEN)
const indicatorOrder = computed(() => (indicatorSide.value === IndicatorSide.RIGHT ? 2 : 0))

// No local selection state anywhere: the shared target variable IS the group,
// so a sibling item's write invalidates this computed with no wiring between
// them. A plain computed also evaluates during server render, so a
// release-baked default prerenders highlighted.
const itemValue = computed(() => resolveOperandValue(fields.value.value, pipe.getData))

const stored = computed(() => (varKey.value ? pipe.getData(`var.${varKey.value}`) : undefined))
const storedList = computed(() => (Array.isArray(stored.value) ? (stored.value as unknown[]) : []))

const isSelected = computed(() =>
  isMulti.value
    ? storedList.value.some((v) => valuesMatch(v, itemValue.value))
    : valuesMatch(stored.value, itemValue.value),
)

const rootEl = ref<HTMLElement | null>(null)
const preselect = usePreselect(pipe, {
  varKey: () => varKey.value,
  candidate: () => ({
    value: itemValue.value,
    priority: fields.value.preselectPriority,
    startSelected: fields.value.selectedByDefault === true,
    multi: isMulti.value,
  }),
  el: () => rootEl.value,
})

// ── Background media ────────────────────────────────────────────────────────
const runtimeConfig = useRuntimeConfig()
const videoReady = ref(false)

const videoUrl = computed(() => {
  const id = fields.value.backgroundVideo?.id
  if (!id) return ''
  const bundledVideos = (runtimeConfig.public as { bundledVideos?: Record<string, string> })
    ?.bundledVideos
  const bundledFile = bundledVideos?.[id]
  if (bundledFile) {
    const base = runtimeConfig.app?.baseURL ?? '/'
    return `${base}videos/${bundledFile}`.replace(/([^:])\/{2,}/g, '$1/')
  }
  return `${import.meta.env.VITE_MEDIA_HOST || ''}/api/media/${id}`
})

watch(
  () => fields.value.backgroundVideo?.id,
  () => {
    videoReady.value = false
  },
)

const imageProps = useImagePreset(() => fields.value.backgroundImage)

const hasVideo = computed(() => !!videoUrl.value)
const hasImage = computed(() => !!imageProps.value)

// The image doubles as the video's poster, so it renders until the video can
// play and then fades out.
const imageShouldRender = computed(() => {
  if (!hasImage.value) return false
  if (!hasVideo.value) return true
  return fields.value.hideImageWhileVideoLoads !== true
})

const imageShouldFadeOut = computed(
  () => hasVideo.value && videoReady.value && fields.value.hideImageWhileVideoLoads !== true,
)

const mediaStyle = computed<CSSProperties>(() => ({
  objectFit: (fields.value.imageObjectFit || 'cover') as CSSProperties['objectFit'],
  objectPosition: fields.value.imageObjectPosition || 'center',
}))

// Clipping is what keeps a background layer inside the border radius, but it
// also clips the children — so a card with neither media nor a full-bleed band
// stays unclipped and an author can still hang a badge over its edge. A band
// reaches the corners, so its background has to be clipped or it squares them
// off.
const rootOverflow = computed(() =>
  hasImage.value || hasVideo.value || hasHeader.value || hasFooter.value ? 'hidden' : 'visible',
)

// Both misconfigurations are otherwise silent: an unset variable makes every
// write a no-op, and a reference that resolves to nothing makes the item
// permanently unselectable.
const wiringHint = computed(() => {
  if (!isEditor.value) return ''
  if (!varKey.value) return 'Pick the choice variable this item writes to'
  if (fields.value.value === undefined || fields.value.value === null)
    return 'Set the value this item writes'
  if (isOperandReference(fields.value.value) && itemValue.value === undefined)
    return 'This value points at a constant or variable that has no value'
  return ''
})

function activate() {
  writeSelection()
  // Last, so a processor that reads the target variable sees this click's value
  // rather than the previous one — but outside writeSelection's guards, so an
  // item with no variable wiring still runs its processors.
  emit('onClick')
}

function writeSelection() {
  const key = varKey.value
  if (!key) return
  const mine = itemValue.value
  if (mine === undefined) return

  if (isMulti.value) {
    // Whole-array replace, and membership/removal share one predicate:
    // Array.includes is SameValueZero, so it would never match two
    // structurally-equal offer groups and a toggle could only ever add.
    const next = isSelected.value
      ? storedList.value.filter((v) => !valuesMatch(v, mine))
      : [...storedList.value, mine]
    pipe.setData(`var.${key}`, next)
    preselect.picked()
    return
  }

  if (isSelected.value && fields.value.allowDeselect === true) {
    // `null`, not `undefined`: undefined is dropped by JSON.stringify on the
    // way into localStorage and the release variable default is re-seeded for
    // any key that is undefined, so the cleared choice would come back on the
    // next load.
    pipe.setData(`var.${key}`, null)
    preselect.picked()
    return
  }

  pipe.setData(`var.${key}`, mine)
  preselect.picked(mine)
}

function onKeydown(event: KeyboardEvent) {
  // Children are arbitrary components (TipTap in the builder), so an unguarded
  // handler would swallow a space typed into one AND toggle the item.
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  activate()
}

// Every item in the group runs this, so it answers a question about the GROUP
// ("is anything chosen?"), never about this item. The equality guard is
// load-bearing: each committing setData snapshots the whole variables map, so
// N items would otherwise write N times per click.
watch(
  () => [fields.value.hasSelectionVariable?.key, stored.value] as const,
  ([key, current]) => {
    if (!key) return
    const has = Array.isArray(current)
      ? current.length > 0
      : current !== undefined && current !== null && current !== ''
    if (pipe.getData(`var.${key}`) === has) return
    pipe.setData(`var.${key}`, has)
  },
  { immediate: true },
)

let removed = false
onBeforeUnmount(() => {
  removed = true
})

onMounted(async () => {
  if (fields.value.selectedByDefault !== true) return
  // The persisted-vars merge runs inside a setTimeout(0), so without this a
  // returning visitor's own choice is clobbered by the default.
  await pipe.whenReady()
  // A rule may have hidden this card while the page got ready (the saved
  // answers, or an abandoned-cart order restore, change what the rules show);
  // its value would then be stored with no card on screen showing it.
  if (removed) return
  // Once a card on screen has a priority, usePreselect decides for the whole
  // group, this card included.
  if (preselect.usesPriorities()) return
  const key = varKey.value
  if (!key) return
  const mine = itemValue.value
  if (mine === undefined) return
  const current = pipe.getData(`var.${key}`)
  const isEmpty =
    current === undefined ||
    current === null ||
    current === '' ||
    (Array.isArray(current) && current.length === 0)
  if (!isEmpty) return
  pipe.setData(`var.${key}`, isMulti.value ? [mine] : mine)
})

// Lets a TipTap in the children write {{comp.isSelected}}.
useComponentScopedVariables('OptionSelectItem', () => ({ isSelected: isSelected.value }))

// Fallbacks are copied from OptionSelectBase.vue so an item dropped beside an
// OptionSingleSelect row is pixel-identical. KEEP IN SYNC — nothing gates it.
const overrideBgColor = computed(
  () => getColorStyleData(fields.value.bgColor) ?? 'var(--color-action-tertiary-background)',
)
// Paints above `background-color` and below the media layers (which are
// positioned children), so an opaque gradient hides the colour states — the
// border and the indicator still carry the selection.
const overrideBgGradient = computed(() => getGradientStyleData(fields.value.bgGradient) ?? 'none')
// `hoverBgColor` is unread by the list components; wired here for non-selected
// states only, defaulting to the idle background so omission is a visual no-op.
const overrideHoverBgColor = computed(
  () => getColorStyleData(fields.value.hoverBgColor) ?? overrideBgColor.value,
)
const overrideActiveBgColor = computed(
  () =>
    getColorStyleData(fields.value.activeBgColor) ??
    'var(--color-action-tertiary-background-pressed)',
)
const overrideBorderColor = computed(
  () => getColorStyleData(fields.value.borderColor) ?? 'var(--color-action-tertiary-border)',
)
const overrideActiveBorderColor = computed(
  () =>
    getColorStyleData(fields.value.activeBorderColor) ??
    'var(--color-action-tertiary-border-pressed)',
)
const overrideGapColumns = computed(
  () => getSizeStyleData(fields.value.gapColumns) ?? 'var(--space-16)',
)
const overridePaddingY = computed(
  () => getSizeStyleData(fields.value.paddingY) ?? 'var(--space-16)',
)
const overridePaddingX = computed(
  () => getSizeStyleData(fields.value.paddingX) ?? 'var(--space-16)',
)
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight) ?? '78px')
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '480px')
const overrideBorderRadius = computed(
  () => getSizeStyleData(fields.value.borderRadius) ?? 'var(--radius-xs)',
)
const overrideMargin = computed(() => getSpacingStyleData(fields.value.margin) ?? '0')

// ── Header / footer bands ───────────────────────────────────────────────────
// Both default to the card's own padding, so a band inherits the inset of the
// row above/below it and only needs its own value when the author wants one.
const rowPadding = computed(() => `${overridePaddingY.value} ${overridePaddingX.value}`)
const headerPadding = computed(
  () => getSpacingStyleData(fields.value.headerPadding) ?? rowPadding.value,
)
const footerPadding = computed(
  () => getSpacingStyleData(fields.value.footerPadding) ?? rowPadding.value,
)

// `transparent` rather than the card colour: a band with no background of its
// own must show whatever the card is painting (colour, gradient, media).
const headerBgColor = computed(() => getColorStyleData(fields.value.headerBgColor) ?? 'transparent')
const headerBgColorSelected = computed(
  () => getColorStyleData(fields.value.headerBgColorSelected) ?? headerBgColor.value,
)
const footerBgColor = computed(() => getColorStyleData(fields.value.footerBgColor) ?? 'transparent')
const footerBgColorSelected = computed(
  () => getColorStyleData(fields.value.footerBgColorSelected) ?? footerBgColor.value,
)

// The whole `border-*` shorthand is bound, not just the colour: a transparent
// 1px line still occupies a pixel of height, which would shift the row of every
// band that never asked for a divider.
function dividerRule(color: string | undefined): string {
  return color ? `var(--border-width) solid ${color}` : 'none'
}
const headerDivider = computed(() => dividerRule(getColorStyleData(fields.value.headerBorderColor)))
const headerDividerSelected = computed(() =>
  dividerRule(
    getColorStyleData(fields.value.headerBorderColorSelected) ??
      getColorStyleData(fields.value.headerBorderColor),
  ),
)
const footerDivider = computed(() => dividerRule(getColorStyleData(fields.value.footerBorderColor)))
const footerDividerSelected = computed(() =>
  dividerRule(
    getColorStyleData(fields.value.footerBorderColorSelected) ??
      getColorStyleData(fields.value.footerBorderColor),
  ),
)

// The interactive states already use box-shadow for their inner-shadow
// treatment, so an authored shadow is layered UNDER it rather than replaced —
// otherwise a card with a drop shadow would visibly lose it on hover.
const overrideBoxShadow = computed(() =>
  composeShadow(fields.value.dropShadow, fields.value.innerShadow),
)
const interactiveBoxShadow = computed(() =>
  overrideBoxShadow.value === 'none'
    ? 'var(--shadow-inner-input-hover)'
    : `${overrideBoxShadow.value}, var(--shadow-inner-input-hover)`,
)

// The nine indicator colours resolve against shape-specific defaults, so the
// unset state reproduces OptionSelectBase's radio palette in single mode and
// its checkbox palette in multi mode.
const indicatorDefaults = computed(() =>
  isCheckbox.value
    ? {
        bgIdle: 'var(--color-background-subtle)',
        bgHover: 'var(--color-background-subtle)',
        bgSelected: 'var(--color-brand-secondary)',
        borderIdle: 'var(--color-action-tertiary-border)',
        borderHover: 'var(--color-action-tertiary-border-pressed)',
        borderSelected: 'var(--color-action-tertiary-border-pressed)',
        fillIdle: 'transparent',
        fillHover: 'transparent',
        fillSelected: 'var(--color-icon-inverse)',
      }
    : {
        bgIdle: 'var(--color-background-subtle-accent)',
        bgHover: 'var(--color-background-subtle-accent)',
        bgSelected: 'var(--color-brand-secondary-subtle)',
        borderIdle: 'var(--color-background-subtle-accent)',
        borderHover: 'var(--color-background-subtle-accent)',
        borderSelected: 'var(--color-border-accent)',
        fillIdle: 'transparent',
        fillHover: 'transparent',
        fillSelected: 'var(--color-brand-secondary)',
      },
)
const indicatorBgIdle = computed(
  () => getColorStyleData(fields.value.indicatorBgIdle) ?? indicatorDefaults.value.bgIdle,
)
const indicatorBgHover = computed(
  () => getColorStyleData(fields.value.indicatorBgHover) ?? indicatorDefaults.value.bgHover,
)
const indicatorBgSelected = computed(
  () => getColorStyleData(fields.value.indicatorBgSelected) ?? indicatorDefaults.value.bgSelected,
)
const indicatorBorderIdle = computed(
  () => getColorStyleData(fields.value.indicatorBorderIdle) ?? indicatorDefaults.value.borderIdle,
)
const indicatorBorderHover = computed(
  () => getColorStyleData(fields.value.indicatorBorderHover) ?? indicatorDefaults.value.borderHover,
)
const indicatorBorderSelected = computed(
  () =>
    getColorStyleData(fields.value.indicatorBorderSelected) ??
    indicatorDefaults.value.borderSelected,
)
const indicatorFillIdle = computed(
  () => getColorStyleData(fields.value.indicatorFillIdle) ?? indicatorDefaults.value.fillIdle,
)
const indicatorFillHover = computed(
  () => getColorStyleData(fields.value.indicatorFillHover) ?? indicatorDefaults.value.fillHover,
)
const indicatorFillSelected = computed(
  () =>
    getColorStyleData(fields.value.indicatorFillSelected) ?? indicatorDefaults.value.fillSelected,
)
</script>

<style scoped lang="scss">
// The card chrome and indicator blocks are duplicated from OptionSelectBase.vue
// with the list wrapper collapsed into this root and its card box moved down to
// `.osi-row`. KEEP IN SYNC — no gate detects divergence.

// Deliberately sets no background: `.active` and the interaction states have
// equal specificity, so a background here would beat the selected fill on
// source order and a selected card would read as unselected under the cursor.
// The hover background is applied separately, guarded on :not(.active).
@mixin item-interactive {
  border-color: v-bind(overrideActiveBorderColor);
  box-shadow: v-bind(interactiveBoxShadow);

  .indicator.radio:not(.checked) {
    background-color: v-bind(indicatorBgHover);
    border-color: v-bind(indicatorBorderHover);

    .radio-dot {
      background-color: v-bind(indicatorFillHover);
    }
  }

  .indicator.checkbox:not(.checked) {
    background-color: v-bind(indicatorBgHover);
    border-color: v-bind(indicatorBorderHover);

    svg {
      stroke: v-bind(indicatorFillHover);
    }
  }
}

.option-select-item {
  // Both exist for the background media: the layers are absolutely positioned
  // against this box, and clipping keeps them inside the border radius.
  position: relative;
  overflow: v-bind(rootOverflow);
  box-sizing: border-box;
  // A column of [header?, row, footer?]. The card's padding, gap and min-height
  // sit on `.osi-row`, NOT here — that is what lets a band reach both edges.
  // With no band the root's height is the row's height, so the box is identical
  // to the single-row version this replaced.
  display: flex;
  flex-direction: column;
  max-width: v-bind(overrideMaxWidth);
  margin: v-bind(overrideMargin);
  cursor: pointer;
  background-color: v-bind(overrideBgColor);
  background-image: v-bind(overrideBgGradient);
  border-width: var(--border-width);
  border-style: solid;
  border-color: v-bind(overrideBorderColor);
  border-radius: v-bind(overrideBorderRadius);
  box-shadow: v-bind(overrideBoxShadow);
  transition: all 0.2s ease;

  // Stacking context per band/row, so the media layers stay behind all three.
  .osi-row {
    position: relative;
    z-index: 3;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: v-bind(overrideGapColumns);
    min-height: v-bind(overrideMinHeight);
    padding: v-bind(overridePaddingY) v-bind(overridePaddingX);
  }

  .osi-band {
    position: relative;
    z-index: 3;
    box-sizing: border-box;
    text-align: start;
  }

  .osi-header {
    padding: v-bind(headerPadding);
    background-color: v-bind(headerBgColor);
    border-bottom: v-bind(headerDivider);
  }

  .osi-footer {
    padding: v-bind(footerPadding);
    background-color: v-bind(footerBgColor);
    border-top: v-bind(footerDivider);
  }

  // Both sit behind the indicator (z-index 4) and the content (3).
  .osi-bg-image,
  .osi-bg-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  .osi-bg-image {
    opacity: 1;
    transition: opacity 0.4s ease-in;

    &.is-hidden {
      opacity: 0;
    }
  }

  .osi-content {
    order: 1;
    flex: 1;
    min-width: 0;
    text-align: start;
    z-index: 3;
  }

  .osi-hint {
    margin: 0;
    font-family: var(--text-body-sm-family);
    font-size: var(--text-body-sm-size);
    color: var(--color-action-tertiary-text);
    opacity: 0.6;
  }

  &.active {
    border-color: v-bind(overrideActiveBorderColor);
    background-color: v-bind(overrideActiveBgColor);

    .osi-header {
      background-color: v-bind(headerBgColorSelected);
      border-bottom: v-bind(headerDividerSelected);
    }

    .osi-footer {
      background-color: v-bind(footerBgColorSelected);
      border-top: v-bind(footerDividerSelected);
    }
  }

  &.no-select {
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    width: 1.5rem;
    flex-shrink: 0;
    border-width: var(--border-width);
    border-style: solid;
    transition:
      background-color 0.2s,
      border-color 0.2s;
    z-index: 4;
  }

  .indicator.radio {
    border-radius: 50%;
    background-color: v-bind(indicatorBgIdle);
    border-color: v-bind(indicatorBorderIdle);

    .radio-dot {
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background-color: v-bind(indicatorFillIdle);
      transform: scale(0);
      transition: all 0.2s ease;
    }

    &.checked {
      background-color: v-bind(indicatorBgSelected);
      border-color: v-bind(indicatorBorderSelected);

      .radio-dot {
        background-color: v-bind(indicatorFillSelected);
        transform: scale(1);
      }
    }
  }

  .indicator.checkbox {
    border-radius: var(--radius-xs);
    background-color: v-bind(indicatorBgIdle);
    border-color: v-bind(indicatorBorderIdle);

    svg {
      opacity: 0;
      transform: scale(0.5);
      transition: all 0.2s ease;
      stroke: v-bind(indicatorFillIdle);
    }

    &.checked {
      background-color: v-bind(indicatorBgSelected);
      border-color: v-bind(indicatorBorderSelected);

      svg {
        opacity: 1;
        transform: scale(1);
        stroke: v-bind(indicatorFillSelected);
      }
    }
  }

  // Gated on a hover-capable pointer: on touch the browser emulates `:hover`
  // after a tap and the state sticks past the navigation.
  @media (hover: hover) {
    &:hover {
      @include item-interactive;
    }
  }

  &:focus-visible {
    outline: none;
    @include item-interactive;
  }

  &:active {
    box-shadow: v-bind(interactiveBoxShadow);
  }

  // `hoverBgColor` is declared in both list definitions and read by neither, so
  // it is wired here. A selected card keeps its selected fill.
  @media (hover: hover) {
    &:hover:not(.active) {
      background-color: v-bind(overrideHoverBgColor);
    }
  }

  &:focus-visible:not(.active) {
    background-color: v-bind(overrideHoverBgColor);
  }
}
</style>
